import * as THREE from 'three';
import { Scene } from '../Core/Scene';
import { Camera } from '../Core/Camera';
import { SectionManager } from './SectionManager';
import { Sizes } from '../Core/Sizes';
import { SmoothScroll } from '../Core/SmoothScroll';
import Time from '../Core/Time';
import EventEmitter from '../Utils/EventEmitter';
import vertexShader from '../shaders/gt-logo/vertex.glsl';
import fragmentShader from '../shaders/gt-logo/fragment.glsl';
import bgVertexShader from '../shaders/gt-logo-bg/vertex.glsl';
import bgFragmentShader from '../shaders/gt-logo-bg/fragment.glsl';
import Resources from '../Utils/Resources';

/**
 * SectionLogo class
 * Creates a logo plane that sticks to the top-right of section 3 and follows
 * the camera until it reaches section 4
 */
export class SectionLogo extends EventEmitter {
  // Static constants for logo sizing
  static LOGO_WIDTH_PERCENTAGE = 11.67; // Logo width as percentage of screen width
  static LOGO_WIDTH_PERCENTAGE_MOBILE = 30;
  static LOGO_MAX_SIZE_PX = 168; // Maximum logo size in pixels

  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {SectionLogo} The singleton instance
   */
  static getInstance() {
    if (!SectionLogo.instance) {
      SectionLogo.instance = new SectionLogo();
    }
    return SectionLogo.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (SectionLogo.instance) {
      return SectionLogo.instance;
    }

    super();

    this.currentProgressLogo = 0;
    this.resources = Resources.getInstance();

    // Setup dependencies
    this.scene = Scene.getInstance();
    this.camera = Camera.getInstance();
    this.sizes = Sizes.getInstance();
    this.scroll = SmoothScroll.getInstance();
    this.sectionManager = SectionManager.getInstance();
    this.time = Time.getInstance();

    // Initialize properties
    this.visible = false;
    this.stickyActive = false;
    this.padding = 0.7; // Padding in world units
    this.paddingMobile = 0.3;
    this.zPosition = 0.01; // Z position constant
    this.bgZPosition = 0.005; // Background Z position (behind logo)
    this.bgScale = 1.8; // Background scale relative to logo
    this.bgColor = new THREE.Color(0x00D4FF); // Background color
    this.bgIntensity = 1.0; // Background gradient intensity
    this.bgOpacity = 0.0; // Start with background invisible
    this.highlightBegin = 0.8; // Start fade in at 80% of scroll to sticky
    this.highlightEnd = 0.95; // Full opacity at 95% of scroll to sticky

    // Create group
    this.group = new THREE.Group();
    this.group.name = `${this.constructor.name}-Group`;
    this.scene.add(this.group);

    // Source and target sections
    this.sourceSection = 2; // Third section (0-indexed)

    // Pre-calculated cache values
    this.logoSize = 0;
    this.paddedOffsetX = 0;
    this.paddedOffsetY = 0;
    this.sourceSectionRight = 0;
    this.sourceSectionTop = 0;

    this.scrollData = null;
    this.acceleration = 0.1;
    this.accelerationTarget = 0.1;
    this.maxAcceleration = 1.2;
    this.minAcceleration = 0.1;
    this.accelerationLerpFactor = 0.03;

    this.bindAll();

    this.setupEventListeners();

    this.resources.on('resource', (resourceName) => {
        if(resourceName === 'gtLogo'){
            this.init();
        }
    });

    // Set instance
    SectionLogo.instance = this;
  }

  bindAll() {
    this.updatePosition = this.updatePosition.bind(this);
    this.updateAnimation = this.updateAnimation.bind(this);
    this.dispose = this.dispose.bind(this);
  }

  init() {
     this.createLogoPlane();
     this.updatePosition();
  }

  calculateAcceleration() {
    if(!this.scrollData) return;

    const speed = Math.abs(this.scrollData.speed);
    let acceleration = speed * 0.1;
    acceleration = Math.min(this.maxAcceleration, acceleration);
    acceleration = Math.max(this.minAcceleration, acceleration);
    this.accelerationTarget = acceleration;
  }

  updateAcceleration() {
    if(!this.accelerationTarget) return;

    this.acceleration = THREE.MathUtils.lerp(this.acceleration, this.accelerationTarget, this.accelerationLerpFactor);
  }

  /**
   * Create logo plane
   */
  createLogoPlane() {
    const planeGeometry = new THREE.PlaneGeometry(1, 1);

    // Create background plane with radial gradient
    this.bgMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uIntensity: { value: this.bgIntensity },
            uColor: { value: this.bgColor },
            uOpacity: { value: this.bgOpacity },
        },
        vertexShader: bgVertexShader,
        fragmentShader: bgFragmentShader,
        transparent: true,
        depthTest: false,
    });

    // Create background mesh
    this.bgPlane = new THREE.Mesh(planeGeometry, this.bgMaterial);
    this.bgPlane.name = `${this.constructor.name}-BgPlane`;
    this.bgPlane.position.z = this.bgZPosition;

    // Add background to group
    this.group.add(this.bgPlane);

    // Create material with shader
    this.logoMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uDiffuseTexture: { value: this.resources.items.gtLogo },
            uProgress: { value: this.currentProgressLogo },
            uOpacity: { value: 1.0 },
        },
        vertexShader,
        fragmentShader,
        transparent: true
    });

    // Create mesh
    this.logoPlane = new THREE.Mesh(planeGeometry, this.logoMaterial);
    this.logoPlane.name = `${this.constructor.name}-LogoPlane`;
    this.logoPlane.position.z = this.zPosition;

    // Add to group
    this.group.add(this.logoPlane);
  }


  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Update on scroll
    this.scroll.on('scroll', (scrollData) => {
      this.scrollData = scrollData;
      this.calculateAcceleration();
      this.updatePosition();
    });

    // Update on resize
    this.sizes.on('resize', () => {
      this.updatePosition();
    });

    // Check layout changes
    this.sectionManager.on('layoutUpdated', () => {
      this.updatePosition();
    });

    // Update on animation frame
    this.time.on('tick', this.updateAnimation);
  }

  /**
   * Get section data
   */
  getSectionData() {
    const sections = this.sectionManager.getSections();
    const sourceSection = sections[this.sourceSection];

    return { sourceSection };
  }

  /**
   * Calculate section-dependent constants
   * @param {Object} sourceSection - Source section data
   */
  calculateConstants(sourceSection) {
    // Calculate logo size based on screen width percentage with max size limit
    const screenWidthPx = this.sizes.width;
    const logoWidth = this.sizes.isMobile ? SectionLogo.LOGO_WIDTH_PERCENTAGE_MOBILE : SectionLogo.LOGO_WIDTH_PERCENTAGE;
    const logoSizePx = Math.min(
      (screenWidthPx * logoWidth) / 100,
      SectionLogo.LOGO_MAX_SIZE_PX
    );

    // Convert from pixels to world units
    const newLogoSize = this.sizes.pixelsToWorldUnits(logoSizePx);

    // If logo size hasn't changed, no need to recalculate
    if (Math.abs(this.logoSize - newLogoSize) < 0.001) return;

    // Update logo size (maintaining 1:1 aspect ratio)
    this.logoSize = newLogoSize;
    this.logoPlane.scale.set(this.logoSize, this.logoSize, 1);

    // Update background size (1.5x the logo size)
    const bgSize = this.logoSize * this.bgScale;
    this.bgPlane.scale.set(bgSize, bgSize, 1);

    // Update precalculated values
    const halfLogoSize = this.logoSize / 2;
    this.paddedOffsetY = this.padding + halfLogoSize;
    this.paddedOffsetX = this.sizes.isMobile ? this.paddingMobile + halfLogoSize : this.paddedOffsetY;

    // Cache section positions that don't change during scroll
    this.sourceSectionRight = sourceSection.worldData.width / 2;
    this.sourceSectionTop = sourceSection.worldData.position.y + sourceSection.worldData.height / 2;
  }

  /**
   * Update position based on scroll
   */
  updatePosition() {
    if(!this.logoPlane) return;
    // Get sections data
    const { sourceSection } = this.getSectionData();

    // If section isn't available yet, return
    if (!sourceSection) return;

    // Calculate and cache constants if necessary
    this.calculateConstants(sourceSection);

    // Calculate frustum dimensions - these change with camera position during scroll
    const frustumHeight = this.sizes.frustumSize.height;
    const frustumWidth = this.sizes.frustumSize.width;
    const frustumTop = this.camera.instance.position.y + frustumHeight / 2;
    const frustumRight = this.camera.instance.position.x + frustumWidth / 2;

    // Initial position at top-right of source section (using cached values)
    const initialX = this.sourceSectionRight - this.paddedOffsetX;
    const initialY = this.sourceSectionTop - this.paddedOffsetY;

    // Calculate sticky position (fixed to top-right of viewport)
    const stickyX = frustumRight - this.paddedOffsetX;
    const stickyY = frustumTop - this.paddedOffsetY;

    // Determine if sticky behavior should be active
    this.stickyActive = initialY > stickyY;

    let progressToSticky = Math.min(.9, Math.max(0, (stickyY - initialY) / 3 + 1));

    this.bgOpacity = progressToSticky; // Invert the opacity
    if (this.bgMaterial) {
      this.bgMaterial.uniforms.uOpacity.value = this.bgOpacity;
    }

    let finalX, finalY;

    if (this.stickyActive) {
      finalX = stickyX;
      finalY = stickyY;
    } else {
      finalX = initialX;
      finalY = initialY;
    }

    // Update position
    this.group.position.set(finalX, finalY, this.zPosition);

    // Update visibility
    const wasVisible = this.visible;

    // Visible when source section is visible
    this.visible = sourceSection.visible;
    this.group.visible = this.visible;

    // Trigger event if visibility changed
    if (wasVisible !== this.visible) {
      this.trigger('visibilityChanged', [this.visible]);
    }
  }

  /**
   * Update animation
   */
  updateAnimation() {
    if (!this.visible) return;

    this.updateAcceleration();

    const delta = this.time.delta;

    this.currentProgressLogo += delta * this.acceleration;
    if(this.currentProgressLogo > 1) {
        this.currentProgressLogo = 0;
    }

    if(this.logoMaterial) {
        this.logoMaterial.uniforms.uProgress.value = this.currentProgressLogo;
    }
  }

  /**
   * Dispose resources
   */
  dispose() {
    // Remove event listeners
    this.scroll.off('scroll');
    this.sizes.off('resize');
    this.sectionManager.off('layoutUpdated');
    this.time.off('tick');

    // Dispose geometry and materials
    if (this.logoPlane) {
      this.logoPlane.geometry.dispose();
      this.logoMaterial.dispose();
      this.group.remove(this.logoPlane);
      this.logoPlane = null;
      this.logoMaterial = null;
    }

    // Dispose background plane
    if (this.bgPlane) {
      this.bgPlane.geometry.dispose();
      this.bgMaterial.dispose();
      this.group.remove(this.bgPlane);
      this.bgPlane = null;
      this.bgMaterial = null;
    }

    // Dispose point light
    if (this.pointLight) {
      this.group.remove(this.pointLight);
      this.pointLight = null;
    }

    // Remove group from scene
    this.scene.remove(this.group);
    this.group = null;

    // Reset instance
    SectionLogo.instance = null;
  }
}
